import React from 'react';

export interface ScrollOverflow {
  /* Attach to the scrolling element */
  ref: React.RefObject<HTMLDivElement | null>;
  /* There is content scrolled past on that side — what a fade at that edge is
     there to say */
  hasBefore: boolean;
  hasAfter: boolean;
}

/* Whether a horizontally scrolling element has content out of sight on either
 * side, so it can be marked as scrollable rather than looking cut off.
 *
 * CSS cannot ask this: it depends on the element's scroll position and on how
 * much its content takes, both of which only exist at runtime. The answer is
 * recomputed on scroll and whenever the element or its content is resized —
 * a filter chip added or removed, or the window changing width. */
const useScrollOverflow = (): ScrollOverflow => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = React.useState({ hasBefore: false, hasAfter: false });

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      setEdges((current) => {
        const hasBefore = scrollLeft > 1;
        /* A pixel of slack: fractional widths would otherwise report content
           out of sight on an element scrolled all the way to its end */
        const hasAfter = scrollLeft + clientWidth < scrollWidth - 1;
        return current.hasBefore === hasBefore && current.hasAfter === hasAfter
          ? current
          : { hasBefore, hasAfter };
      });
    };

    measure();
    element.addEventListener('scroll', measure, { passive: true });
    /* Not in every environment the components are rendered in (jsdom, for
       one), where there is nothing to measure anyway */
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) {
      observer.observe(element);
      /* The content's own width matters as much as the element's */
      Array.from(element.children).forEach((child) => observer.observe(child));
    }
    return () => {
      element.removeEventListener('scroll', measure);
      if (observer) observer.disconnect();
    };
  });

  return { ref, hasBefore: edges.hasBefore, hasAfter: edges.hasAfter };
};

export default useScrollOverflow;
